// Shared upload policy for AI file workflows.
// Backend performs the final parser/binary validation; the frontend only blocks
// file types that are clearly not useful as text/document/image evidence.

export const SUPPORTED_UPLOAD_ACCEPT = '';

export const BLOCKED_UPLOAD_EXTENSIONS = [
  '.app',
  '.apk',
  '.avi',
  '.bin',
  '.dmg',
  '.dll',
  '.dylib',
  '.exe',
  '.iso',
  '.mov',
  '.mp3',
  '.mp4',
  '.msi',
  '.rar',
  '.so',
  '.tar',
  '.tar.gz',
  '.tgz',
  '.wav',
  '.webm',
  '.zip',
  '.7z',
];

export const SUPPORTED_UPLOAD_EXTENSIONS = [
  '.pdf',
  '.xlsx',
  '.xls',
  '.docx',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.hwp',
  '.hwpx',
  '.txt',
  '.md',
  '.csv',
  '.tsv',
  '.json',
  '.jsonl',
  '.xml',
  '.yaml',
  '.yml',
  '.toml',
  '.html',
  '.css',
  '.js',
  '.jsx',
  '.ts',
  '.tsx',
  '.py',
  '.c',
  '.cpp',
  '.cc',
  '.cxx',
  '.h',
  '.hpp',
  '.java',
  '.cs',
  '.go',
  '.rs',
  '.rb',
  '.php',
  '.swift',
  '.kt',
  '.sql',
  '.sh',
  '.bat',
  '.ps1',
  '.ino',
  '.sln',
  '.sin',
  '.ipynb',
];

export const getFileExtension = (fileName = '') => {
  const lowerName = String(fileName || '').toLowerCase();
  const archiveExtension = BLOCKED_UPLOAD_EXTENSIONS.find(ext => lowerName.endsWith(ext));
  if (archiveExtension) return archiveExtension;
  const lastDot = lowerName.lastIndexOf('.');
  return lastDot >= 0 ? lowerName.slice(lastDot) : '';
};

export const isClearlyBlockedUploadExtension = (fileName = '') => {
  const lowerName = String(fileName || '').toLowerCase();
  return BLOCKED_UPLOAD_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

export const isClearlyBlockedUploadMime = (mimeType = '') => {
  const normalized = String(mimeType || '').toLowerCase();
  return normalized.startsWith('video/') || normalized.startsWith('audio/');
};

// 🎙️ AI 채팅에서 받아쓰기(전사)할 수 있는 음성·영상 형식 — 백엔드 services/audio_transcribe_service 와 동일
export const TRANSCRIBABLE_MEDIA_EXTENSIONS = [
  '.mp3', '.m4a', '.aac', '.wav', '.ogg', '.oga', '.opus', '.flac', '.aiff', '.aif', '.weba',
  '.mp4', '.m4v', '.mov', '.webm', '.avi',
];

export const isTranscribableMediaFile = (file) => {
  const lowerName = String(file?.name || '').toLowerCase();
  if (TRANSCRIBABLE_MEDIA_EXTENSIONS.some(ext => lowerName.endsWith(ext))) return true;
  const mime = String(file?.type || '').toLowerCase();
  return (mime.startsWith('audio/') || mime.startsWith('video/')) && !lowerName.endsWith('.mkv');
};

// allowMedia: AI 채팅처럼 음성·영상 파일을 전사 첨부로 받는 화면은 true 로 호출한다.
export const isClearlyBlockedUploadFile = (file, { allowMedia = false } = {}) => {
  if (allowMedia && isTranscribableMediaFile(file)) return false;
  return isClearlyBlockedUploadExtension(file?.name) || isClearlyBlockedUploadMime(file?.type);
};

const AUDIO_VIDEO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.mp4', '.mov', '.avi', '.webm', '.mkv'];

// 음성·영상 파일인지 (차단 사유를 사용자에게 구체적으로 안내하기 위한 분류)
export const isAudioVideoUploadFile = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  if (mime.startsWith('audio/') || mime.startsWith('video/')) return true;
  const lowerName = String(file?.name || '').toLowerCase();
  return AUDIO_VIDEO_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

export const AUDIO_VIDEO_UNSUPPORTED_MESSAGE =
  '음성·영상 파일 받아쓰기는 AI 채팅에서만 할 수 있어요. 여기서는 자막이나 대본 텍스트를 붙여주세요.';

export const MEDIA_LOGIN_REQUIRED_MESSAGE =
  '음성·영상 파일 받아쓰기는 로그인 후 사용할 수 있어요.';

export const getBlockedUploadMessage = (file) => {
  if (isAudioVideoUploadFile(file)) return AUDIO_VIDEO_UNSUPPORTED_MESSAGE;
  const label = file?.type || getFileExtension(file?.name) || '알 수 없는 형식';
  return `압축·실행 파일은 올릴 수 없어요 (${label}). 문서·이미지·코드 파일을 올려주세요.`;
};
